import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FilterBar, FilterField } from '@/components/filters';
import { EMPTY_CUSTOMER_FILTERS, useCustomersStore, type CustomerFilters } from '@/stores/customers.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, date, money } from '@/lib/format';
import { UploadIcon, UsersIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, TableScroll, Table, Tabs, Td, Th, Tr } from '@/components/page';
import { Alert, Button, ButtonLink, EmptyState, Input, Pill, Select, TableSkeleton, cx } from '@/components/ui';
import { useAuthStore } from '@/stores/auth.store';
import { can } from '@/lib/session';
import { AsyncSelect } from '@/components/async-select';
import { customerGroupOptions } from '@/lib/options';
import { CUSTOMER_STAGES, type CustomerStage } from '@ecom/contracts';
import { STAGE_HINTS, STAGE_LABELS, STAGE_TONES, StagePill } from '@/components/customer-stage';

/**
 * The customer directory.
 *
 * Accounts, and — beside them — the people who bought without one, known by
 * the email on their orders: a guest is a customer to support even with no
 * record of their own. Lifetime spend excludes cancelled orders, so the figure
 * here is the same one accounting would arrive at. An account's row opens its
 * full history; a guest's opens their orders.
 */
export function CustomersPage() {
  const t = useT();
  const [view, setView] = useState<'accounts' | 'guests'>('accounts');
  const { exportCsv, exporting } = useCustomersStore();
  const canImport = useAuthStore((state) => can(state.user, 'import:run'));

  return (
    <Page
      title={t('customers')}
      subtitle={view === 'guests' ? t('customersGuestsHint') : t('customersSubtitle')}
      actions={
        view === 'accounts' ? (
          <>
            {/* A merchant arriving from another platform brings their customers (ADR-070). */}
            {canImport ? (
              <ButtonLink to="/import?target=CUSTOMERS" variant="secondary" title={t('importCustomersHint')}>
                <UploadIcon className="size-4" />
                {t('importCustomers')}
              </ButtonLink>
            ) : null}
            {/* The list's own filter as a spreadsheet, built on the server (ADR-064). */}
            <Button variant="secondary" onClick={() => void exportCsv()} disabled={exporting} title={t('exportCustomersHint')}>
              <UploadIcon className="size-4 rotate-180" />
              {exporting ? t('loading') : t('exportCustomers')}
            </Button>
          </>
        ) : null
      }
      fill
    >
      <ListCard>
        <Tabs
          className="px-2"
          label={t('customers')}
          value={view}
          onChange={setView}
          items={[
            { id: 'accounts', label: t('customersAccounts') },
            { id: 'guests', label: t('customersGuests') },
          ]}
        />
        {view === 'accounts' ? <Accounts /> : <GuestBuyers />}
      </ListCard>
    </Page>
  );
}

function GuestBuyers() {
  const { guests, guestsCursor, guestsTotal, guestsLoading, guestsSearch, error, loadGuests, loadMoreGuests } =
    useCustomersStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const navigate = useNavigate();

  useEffect(() => {
    void loadGuests();
  }, [loadGuests]);

  // Their orders are the record there is: the orders list, searched by their email.
  const ordersOf = (email: string) => `/orders?search=${encodeURIComponent(email)}`;

  return (
    <>
      <ListToolbar>
        <FilterBar
          search={guestsSearch}
          onSearch={(search) => void loadGuests(search)}
          placeholder={t('searchGuestsHint')}
          total={guestsTotal ?? undefined}
          shown={guests.length}
        />
      </ListToolbar>

      {error ? (
        <div className="px-4 pt-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      {guestsLoading && guests.length === 0 ? (
        <TableSkeleton columns={5} />
      ) : guests.length === 0 ? (
        <EmptyState title={t('noGuests')} body={t('customersGuestsHint')} icon={<UsersIcon />} />
      ) : (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <Th>{t('customer')}</Th>
                <Th>{t('orderCountShort')}</Th>
                <Th align="end">{t('totalSpent')}</Th>
                <Th align="end">{t('lastOrder')}</Th>
                <Th align="end">{t('firstOrder')}</Th>
              </tr>
            </thead>
            <tbody>
              {guests.map((guest) => (
                <Tr key={guest.email} onClick={() => navigate(ordersOf(guest.email))}>
                  <Td>
                    <Link
                      to={ordersOf(guest.email)}
                      onClick={(event) => event.stopPropagation()}
                      className="block font-medium text-ink hover:text-brand-700 hover:underline"
                    >
                      {guest.name || guest.email}
                    </Link>
                    <span dir="ltr" className="block text-xs text-ink-muted">
                      {guest.email}
                      {guest.phone ? ` · ${guest.phone}` : ''}
                    </span>
                  </Td>
                  <Td data-numeric className="text-ink">
                    {count(guest.orderCount, locale)}
                  </Td>
                  <Td align="end" data-numeric className="font-medium text-ink">
                    {money(guest.totalSpent, locale)}
                  </Td>
                  <Td align="end" className="text-ink-muted">
                    {date(guest.lastOrderAt, locale)}
                  </Td>
                  <Td align="end" className="text-ink-muted">
                    {date(guest.firstOrderAt, locale)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}

      <LoadMore visible={Boolean(guestsCursor)} loading={guestsLoading} onClick={() => void loadMoreGuests()} />
    </>
  );
}

function Accounts() {
  const { items, loading, loadingMore, error, nextCursor, filters, total, tagsInUse, load, loadMore, loadTags, loadStageCounts } =
    useCustomersStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const navigate = useNavigate();

  // A group's members, as its page links here (ADR-085): `?group=<id>`; a stage's, `?stage=at_risk` (ADR-103).
  const [params] = useSearchParams();
  const linkedGroup = params.get('group');
  const linkedStage = CUSTOMER_STAGES.find((stage) => stage === params.get('stage'));
  useEffect(() => {
    if (linkedGroup) {
      void customerGroupOptions.one(linkedGroup).then((group) => load({ groupId: linkedGroup, groupLabel: group?.label ?? '' }));
    } else if (linkedStage) {
      void load({ stage: linkedStage });
    } else {
      void load();
    }
    void loadTags();
    void loadStageCounts();
  }, [load, loadTags, loadStageCounts, linkedGroup, linkedStage]);

  const chips = [
    ...(filters.stage
      ? [{ key: 'stage', label: `${t('customerStage')}: ${t(STAGE_LABELS[filters.stage])}`, onRemove: () => void load({ stage: '' }) }]
      : []),
    ...(filters.groupId
      ? [{ key: 'group', label: `${t('customerGroup')}: ${filters.groupLabel}`, onRemove: () => void load({ groupId: '', groupLabel: '' }) }]
      : []),
    // One chip per tag, so each can be dropped on its own (ADR-064).
    ...filters.tags.map((tag) => ({
      key: `tag-${tag}`,
      label: `${t('customerTags')}: ${tag}`,
      onRemove: () => void load({ tags: filters.tags.filter((entry) => entry !== tag) }),
    })),
    ...(filters.hasOrders
      ? [
          {
            key: 'hasOrders',
            label: filters.hasOrders === 'yes' ? t('filterHasOrders') : t('filterNeverOrdered'),
            onRemove: () => void load({ hasOrders: '' }),
          },
        ]
      : []),
    ...(filters.isActive
      ? [
          {
            key: 'isActive',
            label: filters.isActive === 'yes' ? t('accountActive') : t('inactive'),
            onRemove: () => void load({ isActive: '' }),
          },
        ]
      : []),
    ...(filters.from
      ? [{ key: 'from', label: `${t('filterJoinedFrom')}: ${filters.from}`, onRemove: () => void load({ from: '' }) }]
      : []),
    ...(filters.to
      ? [{ key: 'to', label: `${t('filterJoinedTo')}: ${filters.to}`, onRemove: () => void load({ to: '' }) }]
      : []),
  ];

  return (
    <>
        <StageCounts selected={filters.stage} onSelect={(stage) => void load({ stage })} />
        <ListToolbar>
          <FilterBar
            search={filters.search}
            onSearch={(search) => void load({ search })}
            placeholder={t('searchCustomers')}
            total={total ?? undefined}
            shown={items.length}
            chips={chips}
            onClearAll={() => void load(EMPTY_CUSTOMER_FILTERS)}
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <FilterField label={t('filterOrdering')} htmlFor="c-orders">
                <Select
                  id="c-orders"
                  value={filters.hasOrders}
                  onChange={(event) =>
                    void load({ hasOrders: event.target.value as CustomerFilters['hasOrders'] })
                  }
                >
                  <option value="">{t('anyValue')}</option>
                  <option value="yes">{t('filterHasOrders')}</option>
                  <option value="no">{t('filterNeverOrdered')}</option>
                </Select>
              </FilterField>

              <FilterField label={t('customerStage')} htmlFor="c-stage">
                <Select
                  id="c-stage"
                  value={filters.stage}
                  onChange={(event) => void load({ stage: event.target.value as CustomerStage | '' })}
                >
                  <option value="">{t('anyValue')}</option>
                  {CUSTOMER_STAGES.map((stage) => (
                    <option key={stage} value={stage}>
                      {t(STAGE_LABELS[stage])}
                    </option>
                  ))}
                </Select>
              </FilterField>

              <FilterField label={t('filterAccount')} htmlFor="c-active">
                <Select
                  id="c-active"
                  value={filters.isActive}
                  onChange={(event) =>
                    void load({ isActive: event.target.value as CustomerFilters['isActive'] })
                  }
                >
                  <option value="">{t('anyValue')}</option>
                  <option value="yes">{t('accountActive')}</option>
                  <option value="no">{t('inactive')}</option>
                </Select>
              </FilterField>

              <FilterField label={t('customerTags')} htmlFor="c-tags">
                <Select
                  id="c-tags"
                  value=""
                  onChange={(event) => {
                    const tag = event.target.value;
                    if (tag && !filters.tags.includes(tag)) void load({ tags: [...filters.tags, tag] });
                  }}
                >
                  <option value="">{filters.tags.length > 0 ? t('addTagFilter') : t('anyValue')}</option>
                  {tagsInUse
                    .filter((entry) => !filters.tags.includes(entry.tag))
                    .map((entry) => (
                      <option key={entry.tag} value={entry.tag}>
                        {entry.tag} ({count(entry.customers, locale)})
                      </option>
                    ))}
                </Select>
              </FilterField>

              <FilterField label={t('customerGroup')} htmlFor="c-group">
                <AsyncSelect
                  id="c-group"
                  value={filters.groupId}
                  onChange={(id, option) => void load({ groupId: id, groupLabel: option?.label ?? '' })}
                  fetchPage={customerGroupOptions.page}
                  fetchOne={customerGroupOptions.one}
                  emptyLabel={t('anyValue')}
                />
              </FilterField>

              <FilterField label={t('filterJoinedFrom')} htmlFor="c-from">
                <Input
                  id="c-from"
                  type="date"
                  dir="ltr"
                  value={filters.from}
                  onChange={(event) => void load({ from: event.target.value })}
                />
              </FilterField>

              <FilterField label={t('filterJoinedTo')} htmlFor="c-to">
                <Input
                  id="c-to"
                  type="date"
                  dir="ltr"
                  value={filters.to}
                  onChange={(event) => void load({ to: event.target.value })}
                />
              </FilterField>
            </div>
          </FilterBar>
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading ? (
          <TableSkeleton columns={7} />
        ) : items.length === 0 ? (
          <EmptyState title={t('noCustomers')} body={t('noCustomersHint')} icon={<UsersIcon />} />
        ) : (
          <>
            <TableScroll>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('customer')}</Th>
                    <Th>{t('mobile')}</Th>
                    <Th>{t('customerStage')}</Th>
                    <Th>{t('orderCountShort')}</Th>
                    <Th align="end">{t('totalSpent')}</Th>
                    <Th align="end">{t('lastOrder')}</Th>
                    <Th align="end">{t('joined')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((customer) => (
                    // The whole row opens the customer; the name stays a real
                    // link for a middle-click or a copied address.
                    <Tr key={customer.id} onClick={() => navigate(`/customers/${customer.id}`)}>
                      <Td>
                        <Link
                          to={`/customers/${customer.id}`}
                          onClick={(event) => event.stopPropagation()}
                          className="block font-medium text-ink hover:text-brand-700 hover:underline"
                        >
                          {customer.name || customer.email || customer.phone || '—'}
                        </Link>
                        <span dir="ltr" className="block text-xs text-ink-muted">
                          {customer.email ?? t('noEmail')}
                        </span>
                        {!customer.isActive || customer.tags.length > 0 ? (
                          <span className="mt-1 flex flex-wrap gap-1">
                            {!customer.isActive ? <Pill tone="danger">{t('customerBlocked')}</Pill> : null}
                            {customer.tags.map((tag) => (
                              <Pill key={tag} tone="info">
                                {tag}
                              </Pill>
                            ))}
                          </span>
                        ) : null}
                      </Td>
                      <Td className="whitespace-nowrap text-ink-muted">
                        {customer.phone ? (
                          <span dir="ltr" data-numeric>
                            {customer.phone}
                          </span>
                        ) : (
                          '—'
                        )}
                      </Td>
                      <Td>
                        <StagePill stage={customer.stage} />
                      </Td>
                      <Td data-numeric className="text-ink">
                        {count(customer.orderCount, locale)}
                      </Td>
                      <Td align="end" data-numeric className="font-medium text-ink">
                        {money(customer.totalSpent, locale)}
                      </Td>
                      <Td align="end" className="text-ink-muted">
                        {date(customer.lastOrderAt, locale)}
                      </Td>
                      <Td align="end" className="text-ink-muted">
                        {date(customer.createdAt, locale)}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableScroll>

            {/* Says out loud what the number does and does not include. */}
            <p className="border-t border-line px-5 py-3 text-xs text-ink-muted">{t('spendNote')}</p>
          </>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
    </>
  );
}

/**
 * How many customers stand in each stage now (ADR-103) — the store's shape at
 * a glance, each count a filter. Pressed again, the filter comes off.
 */
function StageCounts({ selected, onSelect }: { selected: CustomerStage | ''; onSelect: (stage: CustomerStage | '') => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const counts = useCustomersStore((state) => state.stageCounts);
  if (counts.length === 0) return null;

  return (
    <section aria-label={t('customerStagesTitle')} className="border-b border-line px-4 py-3">
      <div className="flex flex-wrap gap-2">
        {counts.map(({ stage, customers }) => {
          const on = selected === stage;
          return (
            <button
              key={stage}
              type="button"
              aria-pressed={on}
              title={t(STAGE_HINTS[stage])}
              onClick={() => onSelect(on ? '' : stage)}
              className={cx(
                'flex items-center gap-2 rounded-card border px-3 py-2 text-start transition-colors',
                on ? 'border-brand-600 bg-brand-50' : 'border-line hover:border-ink-muted',
              )}
            >
              <span className={cx('size-2 rounded-full', STAGE_DOTS[STAGE_TONES[stage]])} aria-hidden />
              <span className="text-xs text-ink-muted">{t(STAGE_LABELS[stage])}</span>
              <span data-numeric className="text-sm font-semibold text-ink">
                {count(customers, locale)}
              </span>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-ink-muted">{t('customerStagesHint')}</p>
    </section>
  );
}

const STAGE_DOTS: Record<(typeof STAGE_TONES)[CustomerStage], string> = {
  neutral: 'bg-ink-muted',
  info: 'bg-brand-600',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
};
